import {
  AGGREGATORS,
  DB_AGGREGATORS,
  KNOWN_TRAIN_OPERATORS,
  SBB_OPERATORS,
  SNCF_OPERATORS,
  TRAIN_OPERATORS,
} from '@src/shared/src/const/app';

type LegalNoticeInputs = {
  operators: string[];
  aggregators: string[];
  hasNonExpediaHotelOrRentalItems: boolean;
};

export type ProviderKey = 'other' | 'sbb' | 'sncf' | 'db' | 'expedia';

const normalizeOperator = (operator: string) => operator && operator.trim();
const SNCF_OPERATOR_TOKEN = 'SNCF';
const DB_OPERATOR_PREFIXES: string[] = ['DB '];
const DB_OPERATOR_EXACT: string[] = [TRAIN_OPERATORS.DEUTSCHEBAHN];

const isDbOperator = (operator: string) => {
  const normalized = normalizeOperator(operator);
  if (!normalized) return false;
  if (DB_OPERATOR_EXACT.includes(normalized)) return true;
  return DB_OPERATOR_PREFIXES.some(prefix => normalized.startsWith(prefix));
};

const hasDbProvider = (operators: string[], aggregators: string[]) => (
  operators.includes(TRAIN_OPERATORS.DEUTSCHEBAHN) ||
  operators.some(isDbOperator) ||
  aggregators.some(aggregator => DB_AGGREGATORS.includes(aggregator))
);

const isSncfOperator = (operator: string) => {
  const normalized = normalizeOperator(operator);
  if (!normalized) return false;

  return (
    SNCF_OPERATORS.includes(normalized) ||
    normalized.toUpperCase().includes(SNCF_OPERATOR_TOKEN)
  );
};

const hasSncfOperator = (operators: string[]) => (
  operators.some(isSncfOperator)
);

const hasSbbOperator = (operators: string[]) => (
  operators.some(operator => SBB_OPERATORS.includes(operator))
);

const hasExpediaAggregator = (aggregators: string[]) => (
  aggregators.includes(AGGREGATORS.EXPEDIA_HOTEL)
);

const hasUnknownTrainOperator = (operators: string[]) => (
  operators
    .map(normalizeOperator)
    .filter((operator): operator is string => Boolean(operator))
    .filter(operator => !isDbOperator(operator))
    .filter(operator => !isSncfOperator(operator))
    .some(operator => !KNOWN_TRAIN_OPERATORS.includes(operator))
);

const getLegalNoticeFlags = ({
  operators,
  aggregators,
  hasNonExpediaHotelOrRentalItems,
}: LegalNoticeInputs) => {
  const hasDB = hasDbProvider(operators, aggregators);
  const hasSNCF = hasSncfOperator(operators);
  const hasSBB = hasSbbOperator(operators);
  const hasExpedia = hasExpediaAggregator(aggregators);
  const hasOtherTrainOperator = hasUnknownTrainOperator(operators);
  const hasOther = hasNonExpediaHotelOrRentalItems || hasOtherTrainOperator;

  return {
    hasDB,
    hasSNCF,
    hasSBB,
    hasExpedia,
    hasOther,
  };
};

const buildProviderOrder = (flags: ReturnType<typeof getLegalNoticeFlags>): ProviderKey[] => {
  const order: ProviderKey[] = [];
  if (flags.hasOther) order.push('other');
  if (flags.hasSBB) order.push('sbb');
  if (flags.hasSNCF) order.push('sncf');
  if (flags.hasDB) order.push('db');
  if (flags.hasExpedia) order.push('expedia');
  return order;
};

export const getProviderOrder = (inputs: LegalNoticeInputs): ProviderKey[] => {
  const flags = getLegalNoticeFlags(inputs);
  return buildProviderOrder(flags);
};
